import { useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import classNames from 'classnames';

import { narrativeStatus } from '@helpers';
import {
  DownloadIcon,
  IconButtonV2,
  MeatballsIcon,
  OverlayPopover,
  PencilIcon,
  Popover,
  ShareIcon,
  TopicLibraryCard,
  TrashIcon,
} from '@utilities';

import styles from './_index.module.scss';

const PROCESSING_STATUSES = new Set<string>([
  narrativeStatus.CLEANUP,
  narrativeStatus.NOT_STARTED,
  narrativeStatus.QUEUED,
  narrativeStatus.RUNNING,
  narrativeStatus.SETUP,
]);

export interface NarrativeCardProps {
  allowErrorActions?: boolean;
  createdDatetime?: null | string;
  creatorLabel?: null | string;
  flowId: number | string;
  isActive?: boolean;
  isDeleted?: boolean;
  layout?: 'chat' | 'default';
  name: string;
  onDelete?: () => void;
  onDownloadPpt?: () => void;
  onDownloadXlsx?: () => void;
  onOpen?: () => void;
  onRename?: () => void;
  onShare?: () => void;
  status?: null | string;
  testIdPrefix?: string;
}

const NarrativeCard = ({
  allowErrorActions = false,
  createdDatetime = null,
  creatorLabel = null,
  flowId,
  isActive = false,
  isDeleted = false,
  layout = 'default',
  name,
  onDelete,
  onDownloadPpt,
  onDownloadXlsx,
  onOpen,
  onRename,
  onShare,
  status = null,
  testIdPrefix = 'narrative',
}: NarrativeCardProps) => {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const normalizedStatus = typeof status === 'string' ? status.toUpperCase() : null;
  const isCompleted = normalizedStatus === narrativeStatus.COMPLETED;
  const isProcessing =
    !isDeleted && (normalizedStatus == null || PROCESSING_STATUSES.has(normalizedStatus));
  const isError = !isDeleted && normalizedStatus != null && !isCompleted && !isProcessing;
  const canUseErrorActions = isError && allowErrorActions;
  const hasActionCallbacks = Boolean(
    onDelete || onDownloadPpt || onDownloadXlsx || onRename || onShare
  );
  const hasActions = !isDeleted && (isCompleted || canUseErrorActions) && hasActionCallbacks;
  const isOpenable =
    Boolean(onOpen) && !isDeleted && (isCompleted || isProcessing || canUseErrorActions);
  const shouldShowActionsButton = !isDeleted && (!isError || canUseErrorActions);
  const cardTestId = `${testIdPrefix}-card-${flowId}`;

  const handleOpen = () => {
    if (isOpenable) onOpen?.();
  };

  const handleTitleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleOpen();
  };

  const handleAction = (event: MouseEvent<HTMLElement>, action: (() => void) | undefined) => {
    event.stopPropagation();
    setIsActionsMenuOpen(false);
    action?.();
  };

  const statusContent = isDeleted ? (
    <span data-testid={`${testIdPrefix}-status`}>Deleted</span>
  ) : isError ? (
    <span data-testid={`${testIdPrefix}-status`}>Error</span>
  ) : isProcessing ? (
    <IconButtonV2
      aria-label="Narrative processing"
      className={styles['narrative-processing-indicator']}
      data-testid={`${testIdPrefix}-processing-indicator`}
      disabled
      icon={<span />}
      isLoading
      size="small"
      variant="subtle"
    />
  ) : layout === 'default' ? (
    <span data-testid={`${testIdPrefix}-status`}>{createdDatetime || ''}</span>
  ) : null;
  const metadataLabel = layout === 'chat' ? createdDatetime : creatorLabel;

  return (
    <TopicLibraryCard
      aria-disabled={!isOpenable}
      className={classNames(styles['narrative-card'], styles['has-compact-padding'], {
        [styles['is-deleted']]: isDeleted,
        [styles['is-error']]: isError,
      })}
      data-testid={cardTestId}
      isActive={isActive && !isDeleted && !isError}
      onClick={isOpenable ? handleOpen : undefined}
      tabIndex={-1}
      title={
        isOpenable ? (
          <button
            aria-label={`Open Narrative ${name}`}
            className={styles['narrative-open-button']}
            onClick={handleTitleOpen}
            type="button"
          >
            {name}
          </button>
        ) : (
          name
        )
      }
      titleAccessory={
        shouldShowActionsButton ? (
          <>
            <IconButtonV2
              aria-label="More Actions"
              className={styles['narrative-actions-button']}
              data-testid={`${testIdPrefix}-more-actions-${flowId}`}
              disabled={!hasActions}
              icon={<MeatballsIcon />}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                if (hasActions) setIsActionsMenuOpen(true);
              }}
              ref={actionsButtonRef}
              size="small"
              variant="subtle"
            />
            {hasActions ? (
              <OverlayPopover
                flip={layout === 'chat'}
                hasArrow
                isActive={isActionsMenuOpen}
                onHide={() => setIsActionsMenuOpen(false)}
                target={actionsButtonRef.current}
              >
                <Popover.Menu>
                  <div className={styles['narrative-menu-section-label']} role="presentation">
                    Narrative Actions
                  </div>
                  <Popover.Item
                    aria-label="Rename Narrative"
                    data-testid={`${testIdPrefix}-rename-${flowId}`}
                    icon={<PencilIcon />}
                    onClick={(event: MouseEvent<HTMLElement>) => handleAction(event, onRename)}
                  >
                    <p>Rename</p>
                  </Popover.Item>
                  <Popover.Item
                    aria-label="Share Narrative"
                    data-testid={`${testIdPrefix}-share-${flowId}`}
                    icon={<ShareIcon />}
                    onClick={(event: MouseEvent<HTMLElement>) => handleAction(event, onShare)}
                  >
                    <p>Share</p>
                  </Popover.Item>
                  <Popover.Item
                    aria-label="Delete Narrative"
                    data-testid={`${testIdPrefix}-delete-${flowId}`}
                    icon={<TrashIcon />}
                    onClick={(event: MouseEvent<HTMLElement>) => handleAction(event, onDelete)}
                  >
                    <p>Delete</p>
                  </Popover.Item>
                  <div className={styles['narrative-menu-section-divider']} role="separator" />
                  <div className={styles['narrative-menu-section-label']} role="presentation">
                    Download Options
                  </div>
                  <Popover.Item
                    aria-label="Download Narrative XLSX"
                    data-testid={`${testIdPrefix}-download-xlsx-${flowId}`}
                    icon={<DownloadIcon />}
                    onClick={(event: MouseEvent<HTMLElement>) =>
                      handleAction(event, onDownloadXlsx)
                    }
                  >
                    <p>.XLSX File</p>
                  </Popover.Item>
                  {isCompleted ? (
                    <Popover.Item
                      aria-label="Download Narrative PowerPoint"
                      data-testid={`${testIdPrefix}-download-ppt-${flowId}`}
                      icon={<DownloadIcon />}
                      onClick={(event: MouseEvent<HTMLElement>) =>
                        handleAction(event, onDownloadPpt)
                      }
                    >
                      <p>.PPT File</p>
                    </Popover.Item>
                  ) : null}
                </Popover.Menu>
              </OverlayPopover>
            ) : null}
          </>
        ) : null
      }
      variant="dark"
    >
      <div
        className={classNames(styles['narrative-card-meta'], {
          [styles['has-no-creator']]: !metadataLabel && !isDeleted && !isError,
        })}
      >
        {metadataLabel ? (
          <span
            data-testid={`${testIdPrefix}-${layout === 'chat' ? 'created-date' : 'created-by'}`}
          >
            {metadataLabel}
          </span>
        ) : null}
        {statusContent}
      </div>
    </TopicLibraryCard>
  );
};

export default NarrativeCard;
